import React from 'react';

/**
 * Reusable Input Component.
 * Supports "number" formatting with commas for display only while returning clean values.
 */
const Input = ({
    type = 'text',
    id,
    name,
    label,
    placeholder = '',
    value = '', // Default to empty string to prevent controlled/uncontrolled warnings
    onChange,
    required = false,
    disabled = false,
    step = "1",
    className = '',
    labelClassName = '',
    containerClassName = '',
    readOnly = false,
    error = '',
    trailingIcon,
    ...props
}) => {
    // --- 1. Helper Functions for Number Formatting ---
    const formatNumberWithCommas = (val) => {
        if (val === undefined || val === null || val === '') return '';
        // Strip existing commas first, then format the integer part
        const cleanVal = val.toString().replace(/,/g, '');
        if (isNaN(cleanVal)) return val;

        const parts = cleanVal.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    };

    const stripCommas = (val) => val.replace(/,/g, '');

    // --- 2. Determine Display Value and Input Type ---
    const isNumberType = type === 'number';

    // If it's a number type, format the display value and mask the input type as "text"
    const displayValue = isNumberType ? formatNumberWithCommas(value) : value;
    const actualType = isNumberType ? 'text' : type;

    // --- 3. Intercept Change Event ---
    // const handleInputChange = (e) => {
    //     if (!onChange) return;

    //     if (isNumberType) {
    //         const rawValue = e.target.value;
    //         const cleanValue = stripCommas(rawValue);

    //         // Allow digits, one decimal point, and empty values
    //         if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {
    //             // Create a shallow copy of the event with the cleaned value
    //             const maskedEvent = {
    //                 ...e,
    //                 target: {
    //                     ...e.target,
    //                     name: name,
    //                     value: cleanValue,
    //                 },
    //             };
    //             onChange(maskedEvent);
    //         }
    //     } else {
    //         onChange(e);
    //     }
    // };

    const handleInputChange = (e) => {
        if (!onChange) return;

        if (isNumberType) {
            const rawValue = e.target.value;
            const cleanValue = stripCommas(rawValue);

            // Allow digits, one decimal point, and empty values
            if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {

                // Max check
                if (props.max !== undefined && props.max !== null && cleanValue !== '') {
                    if (parseFloat(cleanValue) > parseFloat(props.max)) {
                        return; // prevent value from exceeding max
                    }
                }
                
                // Create a shallow copy of the event with the cleaned value
                const maskedEvent = {
                    ...e,
                    target: {
                        ...e.target,
                        id: id || e.target.id, // Explicitly pass the ID
                        name: name || e.target.name,
                        type: 'number',        // Explicitly maintain the original type context
                        value: cleanValue,
                    },
                };
                onChange(maskedEvent);
            }
        } else {
            onChange(e);
        }
    };

    const inputBaseStyles = 'w-full px-4 py-2 border rounded-lg ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
        'transition-all duration-200 ease-in-out ' +
        'placeholder-gray-400 text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed';

    const errorStyles = error ? 'border-gmail-red' : 'border-gray-medium';
    const paddingStyles = trailingIcon ? 'pr-10' : 'pr-4';

    return (
        <div className={`mb-4 ${containerClassName}`}>
            {label && (
                <label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <input
                    type={actualType} // Changed from {type}
                    step={step}
                    id={id || name}
                    name={name}
                    placeholder={placeholder}
                    value={displayValue} // Changed from {value}
                    onChange={handleInputChange} // Changed from {onChange}
                    readOnly={readOnly}
                    required={required}
                    disabled={disabled}
                    className={`${inputBaseStyles} ${errorStyles} ${paddingStyles} ${className}`}
                    {...props}
                />
                {trailingIcon && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        {trailingIcon}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Input;